import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, SideDrawer, withStyles, type AccountUserRole, type AccountUserRoleUser } from 'vopay';
import PermissionsSearchField from '../UserPermissionsDrawer/PermissionsSearchField';
import styles from './RoleUsersViewDrawer.module.scss';

const cn = withStyles(styles);

type RoleUsersViewDrawerProps = {
  isOpen: boolean;
  role: AccountUserRole | null;
  users: AccountUserRoleUser[];
  isFetching: boolean;
  onClose: () => void;
};

// The users assigned to the selected role. The list is fetched by the parent so the count in Role Details
// and the list here come from the same response.
export default function RoleUsersViewDrawer({ isOpen, role, users, isFetching, onClose }: RoleUsersViewDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.RoleUsersViewDrawer' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const filteredUsers = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();

    return needle ? users.filter((user) => user.Username.toLowerCase().includes(needle)) : users;
  }, [users, searchTerm]);

  if (!role) return null;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('title', { defaultValue: 'Users' })}
      headerFullWidthContent={
        <PermissionsSearchField
          name="roleUsersSearch"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('searchPlaceholder', { defaultValue: 'Search Users' })}
        />
      }
    >
      <div className={cn('body')}>
        {isFetching && <BodyText weight="md">{t('loading', { defaultValue: 'Loading users...' })}</BodyText>}

        {!isFetching && filteredUsers.length === 0 && (
          <BodyText weight="md" className={cn('emptyState')}>
            {searchTerm
              ? t('noMatches', { defaultValue: 'No users match your search.' })
              : t('noUsers', { defaultValue: 'No users assigned to this role.' })}
          </BodyText>
        )}

        {!isFetching && filteredUsers.length > 0 && (
          <ul className={cn('userList')}>
            {filteredUsers.map((user) => (
              <li key={user.UserID} className={cn('userItem')}>
                {user.Username}
              </li>
            ))}
          </ul>
        )}
      </div>
    </SideDrawer>
  );
}
